/**
 * BUG-08. Catalog's GstRate registry (`GET`/`POST`/`PUT`/`DELETE /api/catalog/gst-rates`) — the
 * list the SKU form's GST rate field picks from. Same shape as `../../packing/ToleranceRules.tsx`
 * (a small, whole-set-in-one-response reference list with its edit form below). The row actions are
 * the app's one ⋮ menu (`RowActions`), Edit and Delete…, on the column layout of
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/category-list/columns.tsx;
 * Delete… confirms through the app's own `useConfirm` dialog.
 *
 * A delete the API refuses (409: a SKU is taxed at this rate) is shown verbatim — the fix it names
 * is to deactivate the rate, which this form's "Active" box does.
 */
import { useState } from "react";
import { useLoaderData, useOutletContext, useRevalidator } from "react-router-dom";
import { z } from "zod";
import toast from "react-hot-toast";
import type { ColumnDef } from "@tanstack/react-table";
import { StatusSwitch } from "../../../components/status-switch";
import { StatusToggle } from "../../../components/table/status-toggle";
import { PiInfoDuotone, PiPencilSimpleDuotone, PiTrashDuotone } from "react-icons/pi";
import { Alert } from "rizzui";
import { WidgetCard, ErrorSurface, EmptyState, Field, FormGroup, Form, problemTitle } from "@steelyard/theme";
import FormFooter from "@core/components/form-footer";
import { PagedTable } from "../../../components/table/data-table";
import { RowActions } from "../../../components/table/row-actions";
import { useConfirm } from "../../../components/confirm-modal";
import { ListHeader } from "../../../components/table/list-header";
import { can } from "../../../lib/permissions";
import { listGstRates, createGstRate, updateGstRate, deleteGstRate, type GstRate } from "../../../lib/services/catalog";
import type { AccountSummary, PermissionState } from "../../../types";

export async function gstRatesListLoader() {
  const rates = await listGstRates(false);
  if (!rates) throw new Error("The API returned no body for the GST rates list.");
  return { rates: rates.items };
}

function rateColumns(
  canAdminister: boolean,
  onEdit: (rate: GstRate) => void,
  onDelete: (rate: GstRate) => void,
): ColumnDef<GstRate>[] {
  const columns: ColumnDef<GstRate>[] = [
    { id: "label", accessorKey: "label", header: "Label" },
    {
      id: "percent",
      accessorFn: (row) => Number(row.percent),
      header: "Percent",
      meta: { align: "right" },
      cell: ({ row }) => <span className="tabular-nums">{row.original.percent}%</span>,
    },
    {
      id: "isActive",
      accessorKey: "isActive",
      header: "Status",
      cell: ({ row }) => {
        const { id, label, percent, isActive } = row.original;
        // No deactivate route for a rate: the status rides PUT with the row's own label and percent.
        return <StatusToggle name={label} active={isActive} onChange={canAdminister ? (next) => updateGstRate(id, { label, percent, isActive: next }) : undefined} />;
      },
    },
  ];

  if (!canAdminister) return columns;

  return [
    ...columns,
    {
      id: "actions",
      header: "",
      enableSorting: false,
      // The row's one ⋮ menu: Edit loads the form below; Delete… asks first (`onDelete` confirms).
      cell: ({ row }) => (
        <RowActions
          name={row.original.label}
          primary={{ label: "Edit rate", icon: PiPencilSimpleDuotone, onClick: () => onEdit(row.original) }}
          more={[{ label: "Delete…", icon: PiTrashDuotone, danger: true, onClick: () => onDelete(row.original) }]}
        />
      ),
    },
  ];
}

const rateSchema = z.object({
  label: z.string().trim().min(1, "Required").max(32),
  percent: z
    .string()
    .trim()
    .regex(/^\d+(\.\d+)?$/, "Enter a number")
    .refine((v) => Number(v) <= 100, "Cannot exceed 100%"),
  isActive: z.boolean(),
});
type RateFormValues = z.infer<typeof rateSchema>;
const blankValues: RateFormValues = { label: "", percent: "", isActive: true };

export default function GstRatesPage() {
  const { rates } = useLoaderData() as Awaited<ReturnType<typeof gstRatesListLoader>>;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const revalidator = useRevalidator();
  const confirm = useConfirm();
  const [error, setError] = useState<unknown>(null);
  const [failedAction, setFailedAction] = useState("save this GST rate");
  const [editing, setEditing] = useState<GstRate | null>(null);
  const [resetValues, setResetValues] = useState<RateFormValues | null>(null);

  const canAdminister = can(permissionState, "catalogue.administer");

  function startEdit(rate: GstRate) {
    setError(null);
    setEditing(rate);
    setResetValues({ label: rate.label, percent: String(rate.percent), isActive: rate.isActive });
  }

  function stopEdit() {
    setEditing(null);
    setResetValues({ ...blankValues });
  }

  async function remove(rate: GstRate) {
    setError(null);
    try {
      setFailedAction(`delete ${rate.label}`);
      await deleteGstRate(rate.id);
      toast.success(`${rate.label} deleted`);
      if (editing?.id === rate.id) stopEdit();
      revalidator.revalidate();
    } catch (err) {
      setError(err);
      toast.error(problemTitle(err, "Could not delete this GST rate"));
    }
  }

  async function submit(values: RateFormValues) {
    setError(null);
    const input = { label: values.label, percent: Number(values.percent) };
    setFailedAction(editing ? `update ${editing.label}` : "add this GST rate");
    try {
      if (editing) {
        await updateGstRate(editing.id, { ...input, isActive: values.isActive });
        toast.success("GST rate updated");
        setEditing(null);
      } else {
        await createGstRate(input);
        toast.success("GST rate added");
      }
      setResetValues({ ...blankValues });
      revalidator.revalidate();
    } catch (err) {
      setError(err);
      toast.error(problemTitle(err, editing ? "Could not update this GST rate" : "Could not add this GST rate"));
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title="GST Rates"
        count={rates.length}
        description="The GST slabs a SKU can be taxed at."
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Catalogue" }, { name: "GST Rates" }]}
      />

      {/* What this list is for, and the two rules that surprise people (2026-09-29, owner request). */}
      <Alert color="info" variant="flat" icon={<PiInfoDuotone aria-hidden className="size-5" />}>
        <ul className="list-disc space-y-1 ps-4 text-sm">
          <li>Every SKU picks one rate from this list; invoices charge GST at that SKU&apos;s rate.</li>
          <li>Changing a percentage applies to invoices raised from now on — invoices already raised keep the rate they were issued at.</li>
          <li>
            A rate a SKU still uses cannot be deleted. <strong>Deactivate</strong> it instead: existing SKUs keep it, and it stops
            being offered when creating or editing a SKU.
          </li>
        </ul>
      </Alert>

      {/* Table and form side by side on a wide screen, so editing a rate never scrolls away from the list. */}
      <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-3">
      <div className="xl:col-span-2">

      {/* No paging endpoint — the whole registry arrives in one response, paged in memory. */}
      <PagedTable<GstRate>
        result={{ items: rates, page: 1, pageSize: rates.length || 1, totalCount: rates.length }}
        caption="GST rates, with their percentage and status"
        columns={rateColumns(canAdminister, startEdit, (rate) =>
          confirm({
            title: `Delete ${rate.label}`,
            consequence: "Only a rate no SKU uses can be deleted. A rate in use can be deactivated instead.",
            confirmLabel: "Delete",
            onConfirm: () => void remove(rate),
          }),
        )}
        getRowId={(row) => row.id}
        empty={<EmptyState title="No GST rates yet">Add the standard slabs before creating a SKU.</EmptyState>}
      />
      </div>

      {canAdminister ? (
        <WidgetCard
          title={editing ? `Edit ${editing.label}` : "Add a GST rate"}
          description={editing ? "Change its label, percentage or status." : "Click a rate in the list to change it."}
          headerClassName="border-muted border-b"
          className="xl:sticky xl:top-24"
        >
          {error ? (
            <div className="p-4 pb-0">
              <ErrorSurface error={error} action={failedAction} />
            </div>
          ) : null}

          <Form<RateFormValues>
            key={editing?.id ?? "new"}
            validationSchema={rateSchema}
            useFormProps={{ defaultValues: resetValues ?? blankValues }}
            resetValues={resetValues}
            className="flex flex-col gap-6 p-4 @container"
            onSubmit={submit}
          >
            {({ register, formState: { errors, isSubmitting } }) => (
              <>
                <FormGroup title="Rate" description="The name shown in the SKU form's dropdown, and the percentage it charges." className="@xl:grid-cols-1 @4xl:grid-cols-1">
                  <Field label="Label" required maxLength={32} placeholder="GST 18%" hint="What admins pick from on a SKU." error={errors.label?.message} {...register("label")} />
                  <Field label="Percent (%)" required inputMode="decimal" placeholder="18" hint="Between 0 and 100." error={errors.percent?.message} {...register("percent")} />
                </FormGroup>

                {/* Its own section, apart from the rate's fields. A new rate is always created active. */}
                {editing ? (
                  <FormGroup title="Status" description="Whether this rate is offered on a SKU." className="@xl:grid-cols-1 @4xl:grid-cols-1">
                    <StatusSwitch activeText="Offered when creating a SKU." inactiveText="Not offered on new SKUs." {...register("isActive")} />
                  </FormGroup>
                ) : null}

                <FormFooter
                  submitBtnText={isSubmitting ? "Saving…" : editing ? "Save changes" : "Add rate"}
                  isLoading={isSubmitting}
                  altBtnText={editing ? "Cancel edit" : "Clear"}
                  handleAltBtn={stopEdit}
                />
              </>
            )}
          </Form>
        </WidgetCard>
      ) : null}
      </div>
    </div>
  );
}
